import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart3, Clock, Target, Zap, Calendar, Download,
  Globe, Flame, Users, LineChart, ChevronDown, FileSpreadsheet, FileText,
} from 'lucide-react';
import { api } from '@/services/client';
import { PageLayout } from '@/components/layout/PageLayout';
import { EmbeddedSkillRail } from '@/components/skill';

type Period = 'day' | 'week' | 'month' | 'year';

interface ProductivityStats {
  tabs_opened: number;
  tabs_closed: number;
  sessions_created: number;
  total_time_seconds: number;
  focus_time_seconds: number;
  productivity_score: number;
  category_breakdown: Record<string, number>;
  domain_breakdown: Record<string, number>;
  recommendations?: string[];
  time_basis?: 'session_seconds' | 'update_counts' | 'estimated_from_tabs';
}

interface TimelineEntry {
  date: string;
  label?: string;
  tabs_opened: number;
}

interface TopSiteRow {
  label?: string;
  domain?: string;
  visits?: number;
  change?: number | null;
  change_available?: boolean;
  trend?: string;
}

interface CategoryRow {
  category?: string;
  value?: number;
  count?: number;
}

const EMPTY_STATS: ProductivityStats = {
  tabs_opened: 0,
  tabs_closed: 0,
  sessions_created: 0,
  total_time_seconds: 0,
  focus_time_seconds: 0,
  productivity_score: 0,
  category_breakdown: {},
  domain_breakdown: {},
  recommendations: [],
};

const CHART_HEIGHT_PX = 200;
const PERIOD_LABEL: Record<Period, string> = {
  day: 'Last 24 hours',
  week: 'Last 7 days',
  month: 'Last 30 days',
  year: 'Last 12 months',
};

const ACTIVITY_TITLE: Record<Period, string> = {
  day: 'Hourly Activity',
  week: 'Daily Activity',
  month: 'Daily Activity',
  year: 'Monthly Activity',
};

function num(raw: unknown): number {
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  if (typeof raw === 'string') {
    const n = parseFloat(raw);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatBreakdownValue(value: number, timeBasis: ProductivityStats['time_basis']): string {
  if (timeBasis === 'session_seconds') return formatDuration(value);
  return `${value} update${value === 1 ? '' : 's'}`;
}

function displayDomain(key: string): string {
  switch (key) {
    case 'unclassified':
      return 'Unclassified';
    case 'local':
      return 'Local';
    default:
      return key;
  }
}

function productivityColor(score: number): string {
  if (score >= 80) return 'text-success-400';
  if (score >= 60) return 'text-info-400';
  if (score >= 40) return 'text-warning-400';
  return 'text-error-400';
}

function productivityBg(score: number): string {
  if (score >= 80) return 'bg-success-500/20';
  if (score >= 60) return 'bg-info-500/20';
  if (score >= 40) return 'bg-warning-500/20';
  return 'bg-error-500/20';
}

const AnalyticsPage: React.FC = () => {
  const [stats, setStats] = useState<ProductivityStats>(EMPTY_STATS);
  const [activity, setActivity] = useState<TimelineEntry[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [topSites, setTopSites] = useState<TopSiteRow[]>([]);
  const [insights, setInsights] = useState<string[]>([]);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [period, setPeriod] = useState<Period>('week');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState<'csv' | 'pdf' | null>(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, tabsRes, trendsRes, sessionsRes] = await Promise.allSettled([
        api.get(`/productivity/stats?period=${period}`),
        api.get('/analytics/enhanced/tabs', { params: { period } }),
        api.get('/analytics/enhanced/trends', { params: { period } }),
        api.get('/analytics/enhanced/sessions', { params: { period } }),
      ]);

      if (statsRes.status === 'fulfilled') {
        const raw = statsRes.value.data || {};
        setStats({
          tabs_opened: num(raw.tabs_opened ?? raw.tabs_managed),
          tabs_closed: num(raw.tabs_closed ?? raw.duplicates_closed),
          sessions_created: num(raw.sessions_created ?? raw.sessions),
          total_time_seconds: num(raw.total_time_seconds),
          focus_time_seconds: num(raw.focus_time_seconds),
          productivity_score: num(raw.productivity_score),
          category_breakdown: raw.category_breakdown ?? {},
          domain_breakdown: raw.domain_breakdown ?? {},
          recommendations: Array.isArray(raw.recommendations) ? raw.recommendations : [],
          time_basis:
            raw.time_basis === 'session_seconds'
              ? 'session_seconds'
              : raw.time_basis === 'estimated_from_tabs'
                ? 'estimated_from_tabs'
                : 'update_counts',
        });
      } else {
        setStats(EMPTY_STATS);
        setError('Could not load analytics stats.');
      }

      if (trendsRes.status === 'fulfilled') {
        const raw = trendsRes.value.data?.trends;
        setActivity(
          Array.isArray(raw)
            ? raw.map((row: Record<string, unknown>, i) => ({
                date: String(row.date ?? row.label ?? i),
                label: row.label ? String(row.label) : undefined,
                tabs_opened: num(row.value),
              }))
            : [],
        );
      } else {
        setActivity([]);
      }

      if (tabsRes.status === 'fulfilled') {
        const data = tabsRes.value.data ?? {};
        setCategories(Array.isArray(data.categories) ? data.categories : []);
        setTopSites(Array.isArray(data.top_sites) ? data.top_sites : []);
        setInsights(Array.isArray(data.insights) ? data.insights.map(String) : []);
      } else {
        setCategories([]);
        setTopSites([]);
        setInsights([]);
      }

      if (sessionsRes.status === 'fulfilled') {
        setSessionTotal(num(sessionsRes.value.data?.total_sessions));
      } else {
        setSessionTotal(0);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError('Could not load analytics.');
      setStats(EMPTY_STATS);
      setActivity([]);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const exportData = async (format: 'csv' | 'pdf') => {
    setExportOpen(false);
    setExporting(format);
    try {
      const response = await api.get('/analytics/export', {
        params: { period, format },
        responseType: 'blob',
      });
      const mime = format === 'pdf' ? 'application/pdf' : 'text/csv';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mime }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_${period}_${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export data:', err);
      setError(`Could not export ${format.toUpperCase()} report. Try again in a moment.`);
    } finally {
      setExporting(null);
    }
  };

  const categoryEntries = useMemo(
    () => Object.entries(stats.category_breakdown).sort(([, a], [, b]) => b - a),
    [stats.category_breakdown],
  );
  const domainEntries = useMemo(
    () => Object.entries(stats.domain_breakdown).sort(([, a], [, b]) => b - a),
    [stats.domain_breakdown],
  );

  const breakdownTotal = useMemo(() => {
    const values = Object.values(stats.domain_breakdown);
    return values.reduce((sum, v) => sum + v, 0) || 1;
  }, [stats.domain_breakdown]);

  const maxActivity = useMemo(
    () => Math.max(1, ...activity.map((e) => e.tabs_opened)),
    [activity],
  );

  const activityTotal = useMemo(
    () => activity.reduce((sum, e) => sum + e.tabs_opened, 0),
    [activity],
  );

  const useActivityList = period === 'month' || period === 'day';

  const hasNoActivity =
    !loading &&
    stats.tabs_opened === 0 &&
    activity.every((t) => t.tabs_opened === 0) &&
    topSites.length === 0;

  if (loading) {
    return (
      <PageLayout title="Analytics" description="Track and analyze your productivity metrics" icon={BarChart3}>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Analytics" description="Track and analyze your productivity metrics" icon={BarChart3}>
      {error && (
        <div className="mb-4 rounded-lg border border-warning-500/30 bg-warning-500/10 px-4 py-3 text-sm text-warning-400">
          {error}
        </div>
      )}

      {hasNoActivity && (
        <div className="mb-4 rounded-lg border border-warning-500/20 bg-warning-500/10 px-4 py-3 text-sm text-warning-400">
          No tab activity in {PERIOD_LABEL[period]}. Sync tabs from the browser extension to populate analytics.
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg p-1">
          {(['day', 'week', 'month', 'year'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                period === p
                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                  : 'text-khiimori-text-muted hover:text-khiimori-text-primary'
              }`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
        <div className="relative">
          <button
            onClick={() => setExportOpen((open) => !open)}
            disabled={exporting !== null}
            className="inline-flex items-center px-4 py-2 border border-khiimori-border/20 rounded-lg text-sm font-medium text-khiimori-text-primary bg-khiimori-bg-elevated/10 hover:bg-khiimori-bg-elevated/20 transition-colors disabled:opacity-60"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? `Exporting ${exporting.toUpperCase()}…` : 'Export'}
            <ChevronDown className="h-4 w-4 ml-2" />
          </button>
          {exportOpen && (
            <div className="absolute right-0 z-20 mt-2 w-52 rounded-xl border border-khiimori-border/20 bg-khiimori-bg-elevated shadow-xl overflow-hidden">
              <button
                type="button"
                onClick={() => exportData('csv')}
                className="flex w-full items-center gap-3 px-4 py-3 text-sm text-khiimori-text-primary hover:bg-khiimori-bg-elevated/20 transition-colors"
              >
                <FileSpreadsheet className="h-4 w-4 text-success-400" />
                <div className="text-left">
                  <div className="font-medium">CSV Spreadsheet</div>
                  <div className="text-xs text-khiimori-text-muted">Raw data for Excel</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => exportData('pdf')}
                className="flex w-full items-center gap-3 px-4 py-3 text-sm text-khiimori-text-primary hover:bg-khiimori-bg-elevated/20 transition-colors border-t border-khiimori-border/10"
              >
                <FileText className="h-4 w-4 text-red-400" />
                <div className="text-left">
                  <div className="font-medium">PDF Report</div>
                  <div className="text-xs text-khiimori-text-muted">Charts & insights</div>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      <EmbeddedSkillRail
        cardClassName="mb-6"
        title="Insight skills"
        restrictCategories={['analyze']}
      />

      {/* Primary stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          label="Productivity Score"
          value={`${stats.productivity_score}%`}
          sub="Based on tab diversity and activity"
          icon={Target}
          valueClass={productivityColor(stats.productivity_score)}
          iconBg={productivityBg(stats.productivity_score)}
        />
        <StatCard
          label="Tracked Time"
          value={stats.total_time_seconds > 0 ? formatDuration(stats.total_time_seconds) : '—'}
          sub={
            stats.total_time_seconds > 0
              ? stats.time_basis === 'estimated_from_tabs'
                ? 'Estimated from tab activity in this period'
                : 'From workspace session totals'
              : 'No session time recorded yet'
          }
          icon={Clock}
          iconBg="bg-info-500/20"
          valueClass="text-khiimori-text-primary"
          iconClass="text-info-400"
        />
        <StatCard
          label="Tab Updates"
          value={String(stats.tabs_opened)}
          sub={`${stats.tabs_closed} duplicates flagged`}
          icon={BarChart3}
          iconBg="bg-primary-500/20"
          valueClass="text-khiimori-text-primary"
          iconClass="text-primary-400"
        />
        <StatCard
          label="Workspaces"
          value={String(sessionTotal || stats.sessions_created)}
          sub="Active workspace sessions"
          icon={Zap}
          iconBg="bg-warning-500/20"
          valueClass="text-khiimori-text-primary"
          iconClass="text-warning-400"
        />
      </div>

      {/* Category + domain breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ChartCard title="Activity by Category" icon={LineChart}>
          {categoryEntries.length === 0 ? (
            <EmptyChart message="No category data yet — sync tabs from the browser extension." />
          ) : (
            <div className="space-y-4">
              {categoryEntries.slice(0, 6).map(([category, value]) => {
                const pct = (value / breakdownTotal) * 100;
                return (
                  <BarRow
                    key={category}
                    label={category}
                    value={formatBreakdownValue(value, stats.time_basis)}
                    pct={pct}
                    colorClass="bg-primary-500"
                  />
                );
              })}
            </div>
          )}
        </ChartCard>

        <ChartCard title="Top Domains" icon={Globe}>
          {domainEntries.length === 0 ? (
            <EmptyChart message="No domain activity yet — open tabs in your workspace." />
          ) : (
            <div className="space-y-4">
              {domainEntries.slice(0, 6).map(([domain, value], index) => {
                const pct = (value / breakdownTotal) * 100;
                return (
                  <div key={domain} className="flex items-center gap-3">
                    <span className="text-sm text-khiimori-text-muted w-6">#{index + 1}</span>
                    <div className="flex-1">
                      <BarRow
                        label={displayDomain(domain)}
                        value={formatBreakdownValue(value, stats.time_basis)}
                        pct={pct}
                        colorClass="bg-success-500"
                        compact
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ChartCard>
      </div>

      {/* Activity — single chart, calendar-based buckets */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <ChartCard
            title={ACTIVITY_TITLE[period]}
            icon={BarChart3}
            action={
              <span className="flex items-center gap-1 text-sm text-khiimori-text-muted">
                <Calendar className="h-4 w-4" />
                {PERIOD_LABEL[period]}
              </span>
            }
          >
            <p className="text-xs text-khiimori-text-muted mb-4">
              Tab sync/update events per {period === 'day' ? 'hour' : period === 'year' ? 'month' : 'calendar day'}.
              {activityTotal > 0 ? ` ${activityTotal} total in this range.` : ''}
            </p>
            {activity.length === 0 || activity.every((t) => t.tabs_opened === 0) ? (
              <EmptyChart message="No activity in this range yet." />
            ) : useActivityList ? (
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {activity.map((entry, index) => (
                  <BarRow
                    key={`${entry.label ?? entry.date}-${index}`}
                    label={entry.label ?? entry.date}
                    value={`${entry.tabs_opened} update${entry.tabs_opened === 1 ? '' : 's'}`}
                    pct={(entry.tabs_opened / maxActivity) * 100}
                    colorClass="bg-gradient-to-r from-blue-500 to-cyan-500"
                  />
                ))}
              </div>
            ) : (
              <div className="flex items-end gap-2" style={{ height: CHART_HEIGHT_PX }}>
                {activity.map((entry, index) => {
                  const barHeight = Math.max(
                    entry.tabs_opened > 0 ? 8 : 2,
                    (entry.tabs_opened / maxActivity) * (CHART_HEIGHT_PX - 32),
                  );
                  return (
                    <div key={`${entry.date}-${index}`} className="flex-1 flex flex-col items-center justify-end h-full min-w-0 group">
                      <div
                        className="w-full rounded-t bg-gradient-to-t from-primary-600 to-primary-400 transition-all hover:from-primary-500 hover:to-primary-300 relative"
                        style={{ height: barHeight }}
                      >
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-khiimori-bg-secondary text-khiimori-text-primary text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                          {entry.tabs_opened} update{entry.tabs_opened === 1 ? '' : 's'}
                        </div>
                      </div>
                      <span className="text-xs text-khiimori-text-muted mt-2 truncate w-full text-center">
                        {entry.label ?? new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </ChartCard>
        </div>

        <ChartCard title="Tab Categories" icon={Globe}>
          {categories.length === 0 ? (
            <EmptyChart message="No category breakdown yet." />
          ) : (
            <div className="space-y-3">
              {categories.map((cat, index) => {
                const pct = typeof cat.value === 'number' ? cat.value : 0;
                const count = typeof cat.count === 'number' ? cat.count : 0;
                return (
                  <BarRow
                    key={`${cat.category ?? index}`}
                    label={cat.category ?? '—'}
                    value={count > 0 ? `${count} tabs` : `${pct}%`}
                    pct={pct}
                    colorClass="bg-gradient-to-r from-purple-500 to-pink-500"
                  />
                );
              })}
            </div>
          )}
        </ChartCard>
      </div>

      {/* Top sites */}
      <ChartCard title="Top Sites" icon={Flame} className="mb-8">
        {topSites.length === 0 ? (
          <EmptyChart message="No domain activity in this range." />
        ) : (
          <div className="space-y-3">
            {topSites.slice(0, 8).map((site, index) => {
              const label = site.label || site.domain || 'Unclassified';
              const visits = typeof site.visits === 'number' ? site.visits : 0;
              const hasChange =
                site.change_available === true &&
                site.change != null &&
                Number.isFinite(site.change);
              return (
                <div
                  key={`${label}-${index}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-khiimori-bg-elevated/5 hover:bg-khiimori-bg-elevated/10 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 text-sm font-bold text-white">
                      {index + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-khiimori-text-primary truncate">{label}</div>
                      <div className="text-xs text-khiimori-text-muted">
                        {visits} update{visits === 1 ? '' : 's'} · {PERIOD_LABEL[period]}
                      </div>
                    </div>
                  </div>
                  {hasChange && (
                    <span
                      className={`text-sm font-semibold shrink-0 ${
                        (site.change ?? 0) >= 0 ? 'text-success-400' : 'text-error-400'
                      }`}
                    >
                      {(site.change ?? 0) >= 0 ? '+' : ''}
                      {Math.abs(site.change ?? 0).toFixed(0)}%
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ChartCard>

      {/* Insights */}
      <div className="bg-primary-500/10 border border-primary-500/20 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-primary-400 mb-3 flex items-center gap-2">
          <Users className="h-5 w-5" />
          Insights & Recommendations
        </h3>
        <ul className="space-y-2 text-sm text-khiimori-text-secondary">
          {(insights.length > 0
            ? insights
            : stats.recommendations?.length
              ? stats.recommendations
              : [
                  stats.tabs_opened > 0
                    ? `${stats.tabs_opened} tab updates recorded this ${period}.`
                    : `No tab updates recorded this ${period}.`,
                  sessionTotal > 0
                    ? `${sessionTotal} workspace session(s) available.`
                    : 'Create a workspace session in the Live tab to start tracking.',
                  'Group related tabs to reduce context switching.',
                ]
          ).map((line, i) => (
            <li key={i}>• {line}</li>
          ))}
        </ul>
      </div>
    </PageLayout>
  );
};

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  valueClass = 'text-khiimori-text-primary',
  iconBg = 'bg-primary-500/20',
  iconClass = 'text-primary-400',
}: {
  label: string;
  value: string;
  sub: string;
  icon: React.ComponentType<{ className?: string }>;
  valueClass?: string;
  iconBg?: string;
  iconClass?: string;
}) {
  return (
    <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-khiimori-text-muted">{label}</p>
          <p className={`text-3xl font-bold mt-2 ${valueClass}`}>{value}</p>
        </div>
        <div className={`p-3 rounded-full ${iconBg}`}>
          <Icon className={`h-6 w-6 ${iconClass}`} />
        </div>
      </div>
      <div className="mt-4 text-sm text-khiimori-text-muted">{sub}</div>
    </div>
  );
}

function ChartCard({
  title,
  icon: Icon,
  children,
  className = '',
  action,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  className?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className={`bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-khiimori-text-primary flex items-center gap-2">
          <Icon className="h-5 w-5 text-primary-400" />
          {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function BarRow({
  label,
  value,
  pct,
  colorClass,
  compact = false,
}: {
  label: string;
  value: string;
  pct: number;
  colorClass: string;
  compact?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className={`font-medium text-khiimori-text-secondary capitalize ${compact ? 'text-sm' : 'text-sm'}`}>
          {label}
        </span>
        <span className="text-sm text-khiimori-text-muted">{value}</span>
      </div>
      <div className={`w-full bg-khiimori-bg-elevated/10 rounded-full overflow-hidden ${compact ? 'h-1.5' : 'h-2'}`}>
        <div className={`${colorClass} h-full rounded-full transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
}

function EmptyChart({ message }: { message: string }) {
  return <p className="text-sm text-khiimori-text-muted py-6">{message}</p>;
}

export default AnalyticsPage;
